import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { API, waLink, CONTACT } from "@/lib/contact";

const PRODUCTS = [
  "Boom Barriers",
  "Speed Breakers",
  "Road Sign Boards",
  "Delineators",
  "Traffic Cones",
  "Reflective Studs",
  "Full Site Fit-Out",
];

const inputCls =
  "w-full bg-[#131B2A] border border-zinc-700 focus:border-amber-500 outline-none text-white placeholder:text-zinc-600 px-4 py-3.5 text-sm transition-colors duration-300";

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export const LeadForm = () => {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", product: PRODUCTS[0], quantity: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      setSubmitted(data);
      toast.success("Inquiry received. Our team will reach out shortly.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const waSummary = (lead) =>
    waLink(
      `Hello ${CONTACT.person}, I just submitted a B2B inquiry on the Rorix website.\n\nName: ${lead.name}\nCompany: ${lead.company}\nProduct: ${lead.product}\nQuantity: ${lead.quantity || "To be discussed"}`
    );

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#0D1220] overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 bg-blueprint opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-amber-400 mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-amber-500 inline-block" /> B2B Inquiry
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-white leading-none" data-testid="contact-heading">
            Spec It.<br />We Ship <span className="text-amber-500">It.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-zinc-400 max-w-md">
            Tell us what your site needs — quantities, timelines, delivery location. You get a
            formal quotation within one working day, or talk to us right now on WhatsApp.
          </p>
          <div className="mt-10 border border-zinc-800 bg-[#0B0F17] p-6">
            <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-zinc-500">Direct Line</p>
            <p className="font-display text-2xl font-bold text-white mt-2">{CONTACT.person}</p>
            <a href={`tel:+91${CONTACT.phone}`} className="font-mono2 text-sm text-amber-400 hover:text-amber-300 transition-colors" data-testid="contact-phone-link">
              {CONTACT.displayPhone}
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-4 flex items-center justify-center gap-2 border border-emerald-500/50 text-emerald-400 font-display font-bold tracking-widest uppercase text-sm px-5 py-3 hover:bg-emerald-500/10 transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          {submitted ? (
            <div className="border border-amber-500/40 bg-[#0B0F17] p-10 h-full flex flex-col justify-center" data-testid="lead-success-panel">
              <span className="w-10 h-10 bg-amber-500 flex items-center justify-center mb-6">
                <ArrowUpRight className="w-5 h-5 text-[#0B0F17]" />
              </span>
              <h3 className="font-display text-3xl font-bold uppercase text-white">Inquiry Logged</h3>
              <p className="mt-3 text-zinc-400 text-sm leading-relaxed max-w-md">
                Reference <span className="font-mono2 text-amber-400">{submitted.id.slice(0, 8).toUpperCase()}</span> —
                our team will revert within one working day. For an instant response, dispatch this
                inquiry straight to WhatsApp.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={waSummary(submitted)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-whatsapp-dispatch"
                  className="clip-notch bg-emerald-500 text-[#0B0F17] font-display font-bold tracking-widest uppercase px-6 py-3.5 flex items-center gap-2 hover:bg-emerald-400 transition-colors duration-300"
                >
                  <MessageCircle className="w-5 h-5" /> Send via WhatsApp
                </a>
                <button
                  onClick={() => { setSubmitted(null); setForm({ name: "", company: "", phone: "", email: "", product: PRODUCTS[0], quantity: "", message: "" }); }}
                  data-testid="lead-new-inquiry-button"
                  className="border border-zinc-600 text-zinc-300 font-display font-bold tracking-widest uppercase px-6 py-3.5 hover:border-amber-500 hover:text-amber-400 transition-colors duration-300"
                >
                  New Inquiry
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-zinc-800 bg-[#0B0F17] p-8 md:p-10" data-testid="lead-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input required placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="lead-name-input" />
                <input required placeholder="Company / Project *" value={form.company} onChange={set("company")} className={inputCls} data-testid="lead-company-input" />
                <input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="lead-phone-input" />
                <input type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="lead-email-input" />
                <select value={form.product} onChange={set("product")} className={inputCls} data-testid="lead-product-select">
                  {PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input placeholder="Approx. Quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} data-testid="lead-quantity-input" />
              </div>
              <textarea
                rows={4}
                placeholder="Site details, timelines, delivery location…"
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} mt-5 resize-none`}
                data-testid="lead-message-input"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-submit-button"
                className="clip-notch mt-6 w-full bg-amber-500 text-[#0B0F17] font-display font-bold tracking-[0.2em] uppercase px-6 py-4 flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Transmitting…" : "Submit Inquiry"} <ArrowUpRight className="w-5 h-5" />
              </button>
              <p className="mt-4 font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                Response within 1 working day · Bulk orders only
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
